// Sub-tasks (Unteraufgaben) — the pure rules behind the detail-panel section.
// No React, no hooks: every rule here is a function of (board, column, row) so a
// test can falsify it without a DOM.
//
// 🔴 "done" has exactly ONE definition in this codebase: a sub-task is done iff its
// cell in the board's WORKFLOW status column holds an option carrying role "closed".
// Never by label. Renaming "Erledigt" → "Abgeschlossen" must not silently reopen five
// tasks — that is precisely why roles exist (Alembic 0011). The backend's
// `subtask_rollups` (services/memos.py) decides it this way, and the Protokoll's
// "2 von 5 erledigt" pill counts it this way; a second definition here would drift
// away from the minutes the moment anyone renames an option.

import { isWorkflowKind } from "@/lib/boardView";
import { isWorkflowStatusColumn, roleOf } from "@/lib/statusRoles";
import type { Board, ColumnDef, Row, StatusOption } from "@/lib/types";

/**
 * What the sub-task rules need from a board: its kind (ticket vs. project) and its
 * columns. `BoardDetail` satisfies this structurally; so does any hand-built fixture.
 * Deliberately NOT the bare `Board` — that type carries no `columns`.
 */
export type SubtaskBoard = Pick<Board, "kind"> & { columns: ColumnDef[] };

/** A column's status options, tolerating the un-configured column. */
function optionsOf(col: ColumnDef | null | undefined): StatusOption[] {
  return col?.config?.options ?? [];
}

/**
 * `isWorkflowStatusColumn` reads a FLAT `{ type, options }` (that is the ViewColumn
 * shape the table and the detail editor hand it). A raw ColumnDef keeps its options
 * under `config`, so adapt the shape — and keep using the ONE predicate. Do not
 * re-derive "is this the workflow column"; there is exactly one answer to that
 * question and it lives in lib/statusRoles.ts.
 */
function isWorkflowCol(col: ColumnDef | null | undefined): boolean {
  if (!col) return false;
  return isWorkflowStatusColumn({ type: col.type, options: optionsOf(col) });
}

/**
 * The board's workflow status column — the ONE whose options carry semantic roles.
 *
 * 🔴 A board can have SEVERAL status columns: "Priorität" and "Bereich" are status
 * columns with NO roles (categories). Taking the first `type === "status"` column
 * would let a sub-task's tick rewrite its PRIORITY. `isWorkflowStatusColumn` is the
 * discriminator (it asks whether ANY option is role-bearing). Mirrors the backend's
 * `subtask_rollups`, which picks the column exactly this way.
 */
export function workflowStatusColumn(board: SubtaskBoard): ColumnDef | null {
  return board.columns.find((c) => c.type === "status" && isWorkflowCol(c)) ?? null;
}

/** The id of the option carrying role "closed" — the ONE meaning of "done". */
export function closedOptionId(col: ColumnDef | null | undefined): string | null {
  return optionsOf(col).find((o) => roleOf(o) === "closed")?.id ?? null;
}

/** The id of the option carrying role "open" — where un-ticking lands. Not `options[0]`. */
export function openOptionId(col: ColumnDef | null | undefined): string | null {
  return optionsOf(col).find((o) => roleOf(o) === "open")?.id ?? null;
}

/**
 * Done iff the row's cell FOR THAT COLUMN holds the closed option. A row with no
 * status cell at all (they exist on prod — a sub-item created from the gutter "+"
 * never gets one) is NOT done.
 */
export function isSubtaskDone(row: Row, col: ColumnDef | null | undefined): boolean {
  const closed = closedOptionId(col);
  if (!col || !closed) return false;
  const value = row.cells?.find((c) => c.column_def_id === col.id)?.value;
  return value === closed;
}

/**
 * 🔴 The checkbox must NEVER write a TICKET board's workflow status.
 *
 * This is the SAME predicate the table cell (`cells/index.tsx`) and the detail editor
 * (`DetailColumnEditor.tsx`) already use — imported, not re-invented. On ticket boards
 * the workflow Status is ACTION-ONLY: every transition carries a comment, a recipient
 * and a notification, and crossing into a closed option stamps `resolved_at`. A
 * checkbox writing that column directly would bypass the whole Phase 10 workflow and
 * silently stamp a resolution. Note BOTH halves matter: a ticket board's CATEGORY
 * status column (Priorität) stays editable.
 */
export function subtaskCheckboxDisabled(
  board: SubtaskBoard,
  col: ColumnDef | null | undefined,
): boolean {
  return isWorkflowKind(board.kind) && isWorkflowCol(col);
}
